import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  createReviewCategory,
  createCondition,
  deleteCondition,
  deleteReviewCategory,
  getAllItemConditions,
  getAllReviewCategories,
  updateReviewCategory,
  updateCondition,
} from "../../services/conditionService";
import { ErrorToaster, SuccessToaster } from "../../helpers/toasterHelper";
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from "../../utils/constants";
import {
  Condition,
  CreateConditionPayload,
  CreateReviewCategoryPayload,
  DeleteConditionPayload,
  DeleteReviewCategoryPayload,
  UpdateConditionPayload,
  UpdateReviewCategoryPayload,
} from "../../types/admin";

interface ConditionState {
  loading: boolean;
  error: string | null;
  conditions: Condition[];
  reviewCategories: Condition[];
}

const initialState: ConditionState = {
  loading: false,
  error: null,
  conditions: [],
  reviewCategories: [],
};

export const fetchConditions = createAsyncThunk(
  "condition/fetchConditions",
  async (_, thunkAPI) => {
    try {
      const response = await getAllItemConditions();
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
      );
    }
  }
);

export const fetchReviewCategories = createAsyncThunk(
  "condition/fetchReviewCategories",
  async (_, thunkAPI) => {
    try {
      const response = await getAllReviewCategories();
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED
      );
    }
  }
);

export const addCondition = createAsyncThunk(
  "condition/addCondition",
  async (
    {
      itemConditionName,
      showSuccess,
    }: CreateConditionPayload,
    thunkAPI
  ) => {
    try {
      const response = await createCondition(itemConditionName);
      if (showSuccess) {
        SuccessToaster(SUCCESS_MESSAGES.CONDITION_CREATED_SUCCESSFULLY);
      }
      thunkAPI.dispatch(fetchConditions());
      return response;
    } catch (error: any) {
      ErrorToaster(ERROR_MESSAGES.FAILED_TO_CREATE_CONDITION);
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_CREATE_CONDITION
      );
    }
  }
);

export const addReviewCategory = createAsyncThunk(
  "condition/addReviewCategory",
  async (
    { reviewCategoryName, reviewType, showSuccess }: CreateReviewCategoryPayload,
    thunkAPI
  ) => {
    try {
      const response = await createReviewCategory(reviewCategoryName, reviewType);
      if (showSuccess) {
        SuccessToaster(SUCCESS_MESSAGES.REVIEW_CATEGORY_CREATED_SUCCESSFULLY);
      }
      thunkAPI.dispatch(fetchReviewCategories());
      return response;
    } catch (error: any) {
      ErrorToaster(ERROR_MESSAGES.FAILED_TO_CREATE_REVIEW_CATEGORY);
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_CREATE_REVIEW_CATEGORY
      );
    }
  }
);

export const editReviewCategory = createAsyncThunk(
  "condition/editReviewCategory",
  async (
    {
      reviewCategoryId,
      reviewCategoryName,
      reviewType,
      showSuccess,
    }: UpdateReviewCategoryPayload,
    thunkAPI
  ) => {
    try {
      const response = await updateReviewCategory(
        reviewCategoryId,
        reviewCategoryName,
        reviewType
      );
      if (showSuccess) {
        SuccessToaster(SUCCESS_MESSAGES.REVIEW_CATEGORY_UPDATED_SUCCESSFULLY);
      }
      thunkAPI.dispatch(fetchReviewCategories());
      return response;
    } catch (error: any) {
      ErrorToaster(ERROR_MESSAGES.FAILED_TO_UPDATE_REVIEW_CATEGORY);
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_UPDATE_REVIEW_CATEGORY
      );
    }
  }
);

export const editCondition = createAsyncThunk(
  "condition/editCondition",
  async (
    {
      itemConditionId,
      itemConditionName,
      showSuccess,
    }: UpdateConditionPayload,
    thunkAPI
  ) => {
    try {
      const response = await updateCondition(itemConditionId, itemConditionName);
      if (showSuccess) {
        SuccessToaster(SUCCESS_MESSAGES.CONDITION_UPDATED_SUCCESSFULLY);
      }
      thunkAPI.dispatch(fetchConditions());
      return response;
    } catch (error: any) {
      ErrorToaster(ERROR_MESSAGES.FAILED_TO_UPDATE_CONDITION);
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_UPDATE_CONDITION
      );
    }
  }
);

export const removeCondition = createAsyncThunk(
  "condition/removeCondition",
  async ({ itemConditionId, showSuccess }: DeleteConditionPayload, thunkAPI) => {
    try {
      const response = await deleteCondition(itemConditionId);
      if (showSuccess) {
        SuccessToaster(SUCCESS_MESSAGES.CONDITION_DELETED_SUCCESSFULLY);
      }
      thunkAPI.dispatch(fetchConditions());
      return response;
    } catch (error: any) {
      ErrorToaster(ERROR_MESSAGES.FAILED_TO_DELETE_CONDITION);
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_DELETE_CONDITION
      );
    }
  }
);

export const removeReviewCategory = createAsyncThunk(
  "condition/removeReviewCategory",
  async (
    { reviewCategoryId, showSuccess }: DeleteReviewCategoryPayload,
    thunkAPI
  ) => {
    try {
      const response = await deleteReviewCategory(reviewCategoryId);
      if (showSuccess) {
        SuccessToaster(SUCCESS_MESSAGES.REVIEW_CATEGORY_DELETED_SUCCESSFULLY);
      }
      thunkAPI.dispatch(fetchReviewCategories());
      return response;
    } catch (error: any) {
      ErrorToaster(ERROR_MESSAGES.FAILED_TO_DELETE_REVIEW_CATEGORY);
      return thunkAPI.rejectWithValue(
        error.message || ERROR_MESSAGES.FAILED_TO_DELETE_REVIEW_CATEGORY
      );
    }
  }
);

const conditionSlice = createSlice({
  name: "condition",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchConditions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConditions.fulfilled, (state, action) => {
        state.loading = false;
        const conditions = Array.isArray(action.payload) ? action.payload : [];
        state.conditions = conditions.map((item: any) => ({
          id: item.itemConditionId || item.id,
          name: item.itemConditionName || item.name,
        }));
      })
      .addCase(fetchConditions.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(fetchReviewCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviewCategories.fulfilled, (state, action) => {
        state.loading = false;
        const reviewCategories = Array.isArray(action.payload)
          ? action.payload
          : [];
        state.reviewCategories = reviewCategories.map((item: any) => ({
          id: item.reviewCategoryId || item.id,
          name: item.reviewCategoryName || item.name,
          reviewType: item.reviewType,
        }));
      })
      .addCase(fetchReviewCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(addCondition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCondition.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addCondition.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(addReviewCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addReviewCategory.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addReviewCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(editReviewCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editReviewCategory.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editReviewCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(editCondition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editCondition.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editCondition.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(removeCondition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCondition.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeCondition.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      })
      .addCase(removeReviewCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeReviewCategory.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeReviewCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          ERROR_MESSAGES.AN_UNEXPECTED_ERROR_OCCURRED;
      });
  },
});

export default conditionSlice.reducer;
export const conditionsSelector = (state: RootState) => state.condition.conditions;
export const reviewCategoriesSelector = (state: RootState) =>
  state.condition.reviewCategories;
